import { useCallback, useMemo, useState } from 'react';
import type { Key } from 'react';
import type { TableRowSelection } from 'antd/es/table/interface';

/**
 * id를 가진 엔티티 목록의 다건 선택 공통 패턴(Ant Table rowSelection + 전체 선택/해제).
 *
 * `useCardSelection`(UseCardView.ts)과 `StockPageContext`가 이 부분을 거의 같게 구현하고
 * 있었다. 가격 워크플로 전용 파생 카운트(하락/상승/미조회 등)는 여기 넣지 않고
 * `useCardSelection`이 이 훅 위에 얹어서 추가한다 — 재고 화면에는 그 개념이 없다.
 */
export function useListSelection<T extends { id: number }>(filtered: T[]) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const onSelectionChange = useCallback((keys: Key[]) => setSelectedIds(keys as number[]), []);

  // rowSelection은 Context value를 거쳐 테이블까지 그대로 전달되므로, 매 렌더마다 새
  // 객체/클로저를 만들면 selectedIds가 안 바뀐 렌더에서도 테이블이 다시 그려진다.
  const rowSelection: TableRowSelection<T> = useMemo(() => ({
    selectedRowKeys: selectedIds,
    onChange: onSelectionChange,
  }), [selectedIds, onSelectionChange]);

  // selectedIds → Set 변환을 한 번만 수행하고 파생값 전체에서 공유한다.
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const allFilteredSelected = useMemo(
    () => filtered.length > 0 && filtered.every((c) => selectedSet.has(c.id)),
    [filtered, selectedSet],
  );

  // ── 전체 선택 스코프 ───────────────────────────────────────────────────────
  // Ant Design 테이블 헤더 체크박스는 "현재 페이지"만 선택한다.
  // 대량 데이터 환경에서 "전체 선택했다"는 착각으로 일부만 처리되는 사고를 막기 위해
  // 항상 명시적인 전체 선택/해제 액션을 제공한다.
  const selectAllFiltered = useCallback(() => {
    setSelectedIds(filtered.map((c) => c.id));
  }, [filtered]);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  return useMemo(() => ({
    selectedIds, setSelectedIds, selectedSet, rowSelection,
    allFilteredSelected, selectAllFiltered, clearSelection,
  }), [selectedIds, selectedSet, rowSelection, allFilteredSelected, selectAllFiltered, clearSelection]);
}
